import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, Zap, RotateCcw, Check, X, AlertCircle, ExternalLink, Package, CheckSquare, Square, PlayCircle, StopCircle, Loader2, Pencil } from 'lucide-react'
import {
  productOptimizerApi,
  OptimizerProductSearchResult,
  OptimizerProductDetail,
  OptimizePreviewResponse,
  JobStatusResponse,
  BatchJobStatus,
  BatchProductStatus
} from '../lib/api'
import { OpenAICostDisplay } from '../components/OpenAICostDisplay'
import AiReadinessBadge, { scoreColor } from '../components/AiReadinessBadge'
import JvsProductHtmlPreview from '../components/JvsProductHtmlPreview'
import type { AiReadiness, MultilangCoherence, ProductFaq } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

// Progress state type
interface ProgressState {
  step: number
  total: number
  message: string
  percentage: number
}

export default function ProductOptimizerPage() {
  const toast = useToast()
  // Search state
  const [searchMode, setSearchMode] = useState<'id' | 'reference'>('id')
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<OptimizerProductSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  // Product state
  const [selectedProduct, setSelectedProduct] = useState<OptimizerProductDetail | null>(null)
  const [isLoadingProduct, setIsLoadingProduct] = useState(false)

  // Optimization state
  const [instructions, setInstructions] = useState('')
  const [warrantyDuration, setWarrantyDuration] = useState('3 años')
  const [aiModel, setAiModel] = useState('gpt-4o-mini')
  const [isGenerating, setIsGenerating] = useState(false)
  const [previewData, setPreviewData] = useState<OptimizePreviewResponse | null>(null)
  const [progress, setProgress] = useState<ProgressState | null>(null)

  // Apply state
  const [isApplying, setIsApplying] = useState(false)
  const [applyProgress, setApplyProgress] = useState<string | null>(null)
  const [applyResult, setApplyResult] = useState<{
    success: boolean
    message: string
    backupId?: number
    openai_usage?: any
    ai_readiness?: AiReadiness | null
    multilang?: MultilangCoherence | null
    cache_purged?: boolean
    hooks_triggered?: boolean
  } | null>(null)

  // Gate AI-ready: 422 del backend (score < 60) → permitir forzar
  const [blockedReadiness, setBlockedReadiness] = useState<AiReadiness | null>(null)
  const [forceApply, setForceApply] = useState(false)

  // Capa GEO editable (direct_answer, FAQ, tags, alt). undefined = usar valor original del preview
  const [geoEdits, setGeoEdits] = useState<{
    direct_answer?: string
    faqs?: ProductFaq[]
    tags?: string
    image_alt?: string
  }>({})

  // Error state for generation
  const [generationError, setGenerationError] = useState<string | null>(null)

  // Inline editing state for preview fields
  const [editingField, setEditingField] = useState<'name' | 'meta_title' | 'meta_description' | 'description_short' | 'description_html' | null>(null)
  const [editedValues, setEditedValues] = useState<{
    name?: string
    meta_title?: string
    meta_description?: string
    description_short?: string
    description_html?: string
  }>({})

  // Job-based async generation
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Batch optimization state
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set())
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [batchJobId, setBatchJobId] = useState<string | null>(null)
  const [batchStatus, setBatchStatus] = useState<BatchJobStatus | null>(null)
  const [batchWarranty, setBatchWarranty] = useState('3 años')
  const [batchAutoApply, setBatchAutoApply] = useState(true)
  const [batchMinScore, setBatchMinScore] = useState(60)
  const [batchTranslate, setBatchTranslate] = useState(true)
  const batchPollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // WebSocket (legacy, kept for backwards compatibility)
  const wsRef = useRef<WebSocket | null>(null)
  const clientIdRef = useRef<string>(`optimizer-${Date.now()}`)

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.hostname}:8000/api/product-optimizer/ws/progress/${clientIdRef.current}`

    try {
      wsRef.current = new WebSocket(wsUrl)

      wsRef.current.onopen = () => {
        console.log('[WS] Connected to optimizer progress')
      }

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'progress') {
            setProgress({
              step: data.step,
              total: data.total,
              message: data.message,
              percentage: data.percentage
            })
          }
        } catch (e) {
          console.error('[WS] Error parsing message:', e)
        }
      }

      wsRef.current.onerror = (error) => {
        console.error('[WS] Error:', error)
      }

      wsRef.current.onclose = () => {
        console.log('[WS] Connection closed')
      }
    } catch (error) {
      console.error('[WS] Failed to connect:', error)
    }
  }, [])

  // Cleanup WebSocket and polling on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
      if (batchPollingRef.current) {
        clearInterval(batchPollingRef.current)
      }
    }
  }, [])

  // Reset edited values when previewData changes
  useEffect(() => {
    setEditedValues({})
    setGeoEdits({})
    setBlockedReadiness(null)
    setForceApply(false)
    setEditingField(null)
  }, [previewData])

  // Helper to get the current value (edited or original)
  const getOptimizedValue = (field: 'name' | 'meta_title' | 'meta_description' | 'description_short' | 'description_html'): string => {
    if (editedValues[field] !== undefined) {
      return editedValues[field]!
    }
    if (!previewData?.optimized) return ''
    return (previewData.optimized as any)[field] || ''
  }

  // Start editing a field
  const startEditing = (field: 'name' | 'meta_title' | 'meta_description' | 'description_short' | 'description_html') => {
    setEditingField(field)
    if (editedValues[field] === undefined) {
      setEditedValues(prev => ({
        ...prev,
        [field]: (previewData?.optimized as any)?.[field] || ''
      }))
    }
  }

  // Save edited value
  const saveEdit = () => {
    setEditingField(null)
  }

  // Cancel editing
  const cancelEdit = (field: 'name' | 'meta_title' | 'meta_description' | 'description_short' | 'description_html') => {
    setEditingField(null)
    // Remove the edited value to revert to original
    setEditedValues(prev => {
      const newValues = { ...prev }
      delete newValues[field]
      return newValues
    })
  }

  // Handle search
  const handleSearch = async () => {
    if (!searchValue.trim()) return

    setIsSearching(true)
    setSearchResults([])
    setSelectedProduct(null)
    setPreviewData(null)
    setApplyResult(null)

    try {
      const results = await productOptimizerApi.search(searchValue, searchMode)
      setSearchResults(results)

      // Auto-select if only one result
      if (results.length === 1) {
        await loadProduct(results[0].id_product)
      }
    } catch (error) {
      console.error('Search error:', error)
    } finally {
      setIsSearching(false)
    }
  }

  // Load product details
  const loadProduct = async (productId: number) => {
    setIsLoadingProduct(true)
    setPreviewData(null)
    setApplyResult(null)

    try {
      const product = await productOptimizerApi.getProduct(productId)
      setSelectedProduct(product)
    } catch (error) {
      console.error('Error loading product:', error)
    } finally {
      setIsLoadingProduct(false)
    }
  }

  // Poll for job status
  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const status = await productOptimizerApi.getPreviewStatus(jobId)

      // Update progress from job status
      if (status.progress) {
        setProgress({
          step: status.progress.step,
          total: status.progress.total,
          message: status.progress.message,
          percentage: status.progress.percentage
        })
      }

      // Check if job is finished
      if (status.status === 'completed' && status.result) {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        setPreviewData(status.result)
        setIsGenerating(false)
        setProgress(null)
        setCurrentJobId(null)
      } else if (status.status === 'failed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        setGenerationError(status.error || 'Error desconocido durante la generación')
        setIsGenerating(false)
        setCurrentJobId(null)
      }
      // If pending or running, continue polling (interval will call this again)
    } catch (error: any) {
      console.error('Error polling job status:', error)
      // Don't stop polling on transient errors, but log them
    }
  }, [])

  // Generate optimized preview using async job-based API
  const handleGenerate = async () => {
    if (!selectedProduct) return

    setIsGenerating(true)
    setProgress(null)
    setPreviewData(null)
    setApplyResult(null)
    setGenerationError(null)

    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    try {
      // Start the async job
      const startResponse = await productOptimizerApi.startPreview({
        product_id: selectedProduct.id_product,
        instructions: instructions,
        warranty_duration: warrantyDuration,
        client_id: clientIdRef.current,
        model: aiModel
      })

      console.log('[Optimizer] Job started:', startResponse.job_id)
      setCurrentJobId(startResponse.job_id)

      // Start polling for status every 3 seconds
      pollingIntervalRef.current = setInterval(() => {
        pollJobStatus(startResponse.job_id)
      }, 3000)

      // Also poll immediately after a short delay
      setTimeout(() => pollJobStatus(startResponse.job_id), 1000)

    } catch (error: any) {
      console.error('Generation error:', error)
      // Extract error message for user display
      let errorMessage = 'Error desconocido al iniciar la generación'
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Error de conexión con el servidor. Intenta de nuevo.'
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.response?.status === 500) {
        errorMessage = 'Error interno del servidor. Revisa los logs del backend.'
      } else if (error.message) {
        errorMessage = error.message
      }
      setGenerationError(errorMessage)
      setIsGenerating(false)
    }
  }

  // Apply changes - Uses the new translate-and-apply flow (Option E)
  // This translates the Spanish content (potentially edited) to all languages and applies
  const handleApply = async () => {
    if (!selectedProduct || !previewData?.optimized) return

    setIsApplying(true)
    setApplyProgress('Preparando traducción a todos los idiomas...')
    setBlockedReadiness(null)

    try {
      // Get the final Spanish values (edited or original) + capa GEO editada
      const geo = getGeoValues()
      const spanishData = {
        name: editedValues.name ?? previewData.optimized.name,
        description_html: editedValues.description_html ?? previewData.optimized.description_html,
        description_short: editedValues.description_short ?? previewData.optimized.description_short,
        meta_title: editedValues.meta_title ?? previewData.optimized.meta_title,
        meta_description: editedValues.meta_description ?? previewData.optimized.meta_description,
        link_rewrite: previewData.optimized.link_rewrite,
        direct_answer: geo.direct_answer,
        faqs: geo.faqs,
        tags: geo.tags,
        image_alt: geo.image_alt,
        features_ps: previewData.optimized.features_ps,
      }

      setApplyProgress('Traduciendo contenido a PT, EN, FR, DE...')

      // Call the new translate-and-apply endpoint
      const result = await productOptimizerApi.translateAndApply({
        product_id: selectedProduct.id_product,
        spanish_data: spanishData,
        json_data: previewData.json_data || {},
        original_link_rewrites: previewData.original_link_rewrites || {},
        mpn: previewData.optimized.mpn,
        warranty_duration: warrantyDuration,
        model: aiModel,
        force: forceApply,
      })

      setApplyProgress(null)
      setApplyResult({
        success: result.success,
        message: result.message,
        backupId: result.backup_id,
        openai_usage: result.openai_usage,
        ai_readiness: result.ai_readiness,
        multilang: result.multilang,
        cache_purged: result.cache_purged,
        hooks_triggered: result.hooks_triggered,
      })
    } catch (error: any) {
      setApplyProgress(null)
      const detail = error.response?.data?.detail
      if (error.response?.status === 422 && detail && typeof detail === 'object' && detail.ai_readiness) {
        // Gate AI-ready: no se ha escrito nada; el usuario puede corregir o forzar
        setBlockedReadiness(detail.ai_readiness)
        toast.error('Ficha no AI-ready', detail.message || 'Score insuficiente')
      } else {
        setApplyResult({
          success: false,
          message: typeof detail === 'string' ? detail : (detail?.message || 'Error al aplicar cambios')
        })
      }
    } finally {
      setIsApplying(false)
    }
  }

  // Valores finales de la capa GEO (editados o del preview)
  const getGeoValues = () => {
    const opt = previewData?.optimized
    const tagsStr = geoEdits.tags ?? (opt?.tags || []).join(', ')
    return {
      direct_answer: geoEdits.direct_answer ?? (opt?.direct_answer || ''),
      faqs: geoEdits.faqs ?? (opt?.faqs || []),
      tags: tagsStr.split(',').map(t => t.trim()).filter(Boolean),
      image_alt: geoEdits.image_alt ?? (opt?.image_alt || ''),
    }
  }
  const wordCount = (s: string) => (s || '').trim().split(/\s+/).filter(Boolean).length

  // Rollback changes
  const handleRollback = async () => {
    if (!applyResult?.backupId) return

    try {
      const result = await productOptimizerApi.rollback(applyResult.backupId)

      if (result.success) {
        setApplyResult({
          success: true,
          message: 'Cambios revertidos correctamente'
        })
        // Clear preview since content is now different
        setPreviewData(null)
        // Reload product
        if (selectedProduct) {
          loadProduct(selectedProduct.id_product)
        }
      }
    } catch (error: any) {
      setApplyResult({
        success: false,
        message: error.response?.data?.detail || 'Error al revertir cambios'
      })
    }
  }

  // Clear all state
  const handleClear = () => {
    // Stop any ongoing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    setSearchValue('')
    setSearchResults([])
    setSelectedProduct(null)
    setPreviewData(null)
    setApplyResult(null)
    setInstructions('')
    setProgress(null)
    setGenerationError(null)
    setCurrentJobId(null)
    setIsGenerating(false)
    setSelectedProductIds(new Set())
  }

  // Toggle product selection
  const toggleProductSelection = (productId: number) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }

  // Select/deselect all products
  const toggleSelectAll = () => {
    if (selectedProductIds.size === searchResults.length) {
      setSelectedProductIds(new Set())
    } else {
      setSelectedProductIds(new Set(searchResults.map(p => p.id_product)))
    }
  }

  // Open batch modal with confirmation
  const openBatchModal = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedProductIds(new Set(searchResults.map(p => p.id_product)))
    }
    setShowBatchModal(true)
  }

  // Poll batch job status
  const pollBatchStatus = useCallback(async (jobId: string) => {
    try {
      const status = await productOptimizerApi.getBatchStatus(jobId)
      setBatchStatus(status)

      if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
        if (batchPollingRef.current) {
          clearInterval(batchPollingRef.current)
          batchPollingRef.current = null
        }
      }
    } catch (error) {
      console.error('Error polling batch status:', error)
    }
  }, [])

  // Start batch optimization
  const startBatchOptimization = async () => {
    if (selectedProductIds.size === 0) return

    try {
      const response = await productOptimizerApi.startBatch({
        product_ids: Array.from(selectedProductIds),
        instructions: instructions, // Use global instructions
        warranty_duration: batchWarranty,
        auto_apply: batchAutoApply,
        model: aiModel,
        min_score: batchMinScore,
        translate: batchTranslate,
      })

      setBatchJobId(response.job_id)

      // Start polling
      batchPollingRef.current = setInterval(() => {
        pollBatchStatus(response.job_id)
      }, 2000)

      // Initial poll
      setTimeout(() => pollBatchStatus(response.job_id), 500)

    } catch (error: any) {
      console.error('Error starting batch:', error)
      toast.error('Error al iniciar optimizacion', error.response?.data?.detail || 'Error desconocido')
    }
  }

  // Cancel batch job
  const cancelBatchJob = async () => {
    if (!batchJobId) return

    try {
      await productOptimizerApi.cancelBatch(batchJobId)
    } catch (error) {
      console.error('Error cancelling batch:', error)
    }
  }

  // Close batch modal
  const closeBatchModal = () => {
    if (batchPollingRef.current) {
      clearInterval(batchPollingRef.current)
      batchPollingRef.current = null
    }
    setShowBatchModal(false)
    setBatchJobId(null)
    setBatchStatus(null)
  }

  // Get current description HTML
  const getCurrentDescriptionHtml = () => {
    if (!selectedProduct) return ''
    const esLang = selectedProduct.languages[1]
    return esLang?.description || ''
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Zap className="w-6 h-6 text-yellow-500" />
          Optimizador de Fichas de Producto
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Mejora la presentación y SEO de productos existentes usando IA
        </p>
      </div>

      {/* Search Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Mode */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="searchMode"
                checked={searchMode === 'id'}
                onChange={() => setSearchMode('id')}
                className="text-primary-600"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">ID de Producto</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="searchMode"
                checked={searchMode === 'reference'}
                onChange={() => setSearchMode('reference')}
                className="text-primary-600"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">Referencia/SKU</span>
            </label>
          </div>

          {/* Search Input */}
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={searchMode === 'id' ? 'Introduce el ID del producto...' : 'Introduce la referencia...'}
              className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={handleSearch}
              disabled={isSearching || !searchValue.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Buscar
            </button>
            {(selectedProduct || searchResults.length > 0) && (
              <button
                onClick={handleClear}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Search Results */}
        {searchResults.length > 1 && (
          <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-4">
            {/* Global Instructions - applies to all optimizations */}
            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <label className="block text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                📝 Instrucciones para la optimización (se aplicarán a todos los productos)
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Ej:
- Cambiar la garantía de 2 años a 3 años en toda la ficha
- Añadir que es compatible con Windows 11
- Corregir el nombre del modelo a 'LP156WH4-TLA1'
- Indicar que el conector es de 40 pines"
                rows={5}
                className="w-full px-4 py-2 border border-amber-300 dark:border-amber-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 text-sm"
              />
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Estas instrucciones se enviarán a la IA para cada producto optimizado, ya sea individual o en lote.
              </p>

              {/* Model Selector */}
              <div className="mt-3 flex items-center gap-3">
                <label className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  🤖 Modelo IA:
                </label>
                <select
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  className="px-3 py-1.5 border border-amber-300 dark:border-amber-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-amber-500"
                >
                  <optgroup label="OpenAI">
                    <option value="gpt-4o-mini">GPT-4o Mini (rápido, económico)</option>
                    <option value="gpt-4o">GPT-4o (mejor calidad)</option>
                  </optgroup>
                  <optgroup label="Google Gemini">
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash (muy rápido, económico)</option>
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro (alta calidad)</option>
                  </optgroup>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Encontrados {searchResults.length} productos
                {selectedProductIds.size > 0 && (
                  <span className="ml-2 text-primary-600 font-medium">
                    ({selectedProductIds.size} seleccionados)
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleSelectAll}
                  className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-primary-600 flex items-center gap-1"
                >
                  {selectedProductIds.size === searchResults.length ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {selectedProductIds.size === searchResults.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </button>
              </div>
            </div>

            {/* Batch Action Buttons */}
            {searchResults.length > 1 && (
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => openBatchModal(true)}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-lg flex items-center gap-2"
                >
                  <Zap className="w-4 h-4" />
                  Optimizar todos ({searchResults.length})
                </button>
                {selectedProductIds.size > 0 && selectedProductIds.size < searchResults.length && (
                  <button
                    onClick={() => openBatchModal(false)}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg flex items-center gap-2"
                  >
                    <Zap className="w-4 h-4" />
                    Optimizar seleccionados ({selectedProductIds.size})
                  </button>
                )}
              </div>
            )}

            <div className="grid gap-2">
              {searchResults.map((result) => (
                <div
                  key={result.id_product}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                    selectedProduct?.id_product === result.id_product
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-primary-300'
                  }`}
                >
                  {/* Checkbox */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleProductSelection(result.id_product)
                    }}
                    className="flex-shrink-0 text-slate-400 hover:text-primary-600"
                  >
                    {selectedProductIds.has(result.id_product) ? (
                      <CheckSquare className="w-5 h-5 text-primary-600" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>

                  {/* Product info - clickable */}
                  <button
                    onClick={() => loadProduct(result.id_product)}
                    className="flex-1 text-left flex items-center gap-3"
                  >
                    <Package className="w-5 h-5 text-slate-400" />
                    <div>
                      <span className="font-medium text-slate-900 dark:text-white">
                        ID: {result.id_product}
                      </span>
                      {result.reference && (
                        <span className="mx-2 text-slate-500">|</span>
                      )}
                      <span className="text-slate-600 dark:text-slate-400">
                        {result.reference}
                      </span>
                      <span className="mx-2 text-slate-500">-</span>
                      <span className="text-slate-700 dark:text-slate-300">
                        {result.name}
                      </span>
                    </div>
                    {!result.active && (
                      <span className="ml-auto px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                        Inactivo
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Selected Product */}
      {isLoadingProduct && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-2 text-slate-600 dark:text-slate-400">Cargando producto...</p>
        </div>
      )}

      {selectedProduct && !isLoadingProduct && (
        <>
          {/* Product Info */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary-600" />
                  {selectedProduct.languages[1]?.name || 'Producto'}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  ID: {selectedProduct.id_product} | Ref: {selectedProduct.reference || 'N/A'}
                </p>
              </div>
              <a
                href={`https://www.jvsinformatica.es/es/${selectedProduct.languages[1]?.link_rewrite || selectedProduct.id_product}.html`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Ver en tienda
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Current Description Preview */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div className="bg-slate-50 dark:bg-slate-900 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="font-medium text-slate-700 dark:text-slate-300">Ficha Actual</span>
              </div>
              <JvsProductHtmlPreview html={getCurrentDescriptionHtml()} maxHeight={384} title="Ficha actual" />
            </div>

            {/* Instructions */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Instrucciones adicionales (opcional)
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Ej: Cambiar duración de garantía de 2 a 3 años, añadir que es compatible con Windows 11..."
                rows={3}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Warranty Duration and Model */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Duración de garantía
                </label>
                <select
                  value={warrantyDuration}
                  onChange={(e) => setWarrantyDuration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option value="6 meses">6 meses</option>
                  <option value="1 año">1 año</option>
                  <option value="2 años">2 años</option>
                  <option value="3 años">3 años</option>
                  <option value="5 años">5 años</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  🤖 Modelo IA
                </label>
                <select
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <optgroup label="OpenAI">
                    <option value="gpt-4o-mini">GPT-4o Mini (rápido, económico)</option>
                    <option value="gpt-4o">GPT-4o (mejor calidad)</option>
                  </optgroup>
                  <optgroup label="Google Gemini">
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash (muy rápido, económico)</option>
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro (alta calidad)</option>
                  </optgroup>
                </select>
              </div>
            </div>

            {/* Generate Button */}
            <div className="mt-6">
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Zap className="w-5 h-5" />
                {isGenerating ? 'Generando...' : 'Generar Nueva Ficha'}
              </button>
            </div>

            {/* Progress Bar - Show during generation */}
            {isGenerating && (
              <div className="mt-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-3 mb-3">
                  <div className="animate-spin w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                  <span className="font-medium text-blue-800 dark:text-blue-200">
                    Generando ficha optimizada...
                  </span>
                </div>
                {progress ? (
                  <>
                    <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
                      <span>Paso {progress.step} de {progress.total}: {progress.message}</span>
                      <span className="font-semibold">{progress.percentage}%</span>
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-3 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-primary-600 dark:text-primary-400">
                    Conectando con el servidor...
                  </div>
                )}
                <p className="text-xs text-primary-600 dark:text-primary-400 mt-3">
                  Este proceso puede tardar 1-2 minutos. Genera la ficha optimizada en español (las traducciones se realizarán al confirmar).
                </p>
              </div>
            )}

            {/* Generation Error */}
            {generationError && !isGenerating && (
              <div className="mt-4 p-4 bg-danger-50 dark:bg-danger-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-800 dark:text-red-200">Error al generar la ficha</h4>
                    <p className="text-sm text-danger-700 dark:text-red-300 mt-1">{generationError}</p>
                    <button
                      onClick={() => setGenerationError(null)}
                      className="text-sm text-danger-600 hover:text-red-800 underline mt-2"
                    >
                      Descartar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Preview Comparison */}
          {previewData && (
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Comparación: Antes vs Después
                </h3>
              </div>

              {/* AI-ready score (capa GEO + SEO) */}
              {previewData.ai_readiness && (
                <AiReadinessBadge readiness={previewData.ai_readiness} className="mb-4" />
              )}

              {/* OpenAI Cost Display */}
              {previewData.openai_usage && (
                <OpenAICostDisplay
                  usage={previewData.openai_usage}
                  showDetails={true}
                  className="mb-4"
                />
              )}

              {/* Name Comparison */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Nombre del producto</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <span className="text-xs font-medium text-danger-600 dark:text-danger-400">ANTES</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{previewData.current.name}</p>
                  </div>
                  <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700 rounded-lg relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-success-600 dark:text-success-400">DESPUÉS</span>
                      {editingField !== 'name' && !applyResult && (
                        <button
                          onClick={() => startEditing('name')}
                          className="p-1 text-success-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                          title="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {editingField === 'name' ? (
                      <div className="mt-1">
                        <input
                          type="text"
                          value={editedValues.name ?? ''}
                          onChange={(e) => setEditedValues(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-green-300 dark:border-green-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                          autoFocus
                        />
                        <div className="flex gap-2 mt-2">
                          <button onClick={saveEdit} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                            <Check className="w-3 h-3" />
                          </button>
                          <button onClick={() => cancelEdit('name')} className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className={`text-sm text-gray-900 dark:text-gray-100 mt-1 ${editedValues.name !== undefined ? 'text-blue-700 dark:text-blue-300 font-medium' : ''}`}>
                        {getOptimizedValue('name')}
                        {editedValues.name !== undefined && <span className="text-xs text-primary-500 ml-2">(editado)</span>}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Meta Title Comparison */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Meta Title (SEO)</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <span className="text-xs font-medium text-danger-600 dark:text-danger-400">ANTES</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                      {previewData.current.meta_title || <span className="text-slate-400 italic">Sin meta title</span>}
                    </p>
                    {previewData.current.meta_title && (
                      <span className="text-xs text-slate-500">{previewData.current.meta_title.length} caracteres</span>
                    )}
                  </div>
                  <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700 rounded-lg relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-success-600 dark:text-success-400">DESPUÉS</span>
                      {editingField !== 'meta_title' && !applyResult && (
                        <button
                          onClick={() => startEditing('meta_title')}
                          className="p-1 text-success-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                          title="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {editingField === 'meta_title' ? (
                      <div className="mt-1">
                        <input
                          type="text"
                          value={editedValues.meta_title ?? ''}
                          onChange={(e) => setEditedValues(prev => ({ ...prev, meta_title: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-green-300 dark:border-green-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                          maxLength={70}
                          autoFocus
                        />
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs ${(editedValues.meta_title?.length || 0) > 70 ? 'text-danger-500' : 'text-slate-500'}`}>
                            {editedValues.meta_title?.length || 0}/70 caracteres
                          </span>
                          <div className="flex gap-2">
                            <button onClick={saveEdit} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                              <Check className="w-3 h-3" />
                            </button>
                            <button onClick={() => cancelEdit('meta_title')} className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className={`text-sm text-gray-900 dark:text-gray-100 mt-1 ${editedValues.meta_title !== undefined ? 'text-blue-700 dark:text-blue-300 font-medium' : ''}`}>
                          {getOptimizedValue('meta_title') || <span className="text-slate-400 italic">Sin meta title</span>}
                          {editedValues.meta_title !== undefined && <span className="text-xs text-primary-500 ml-2">(editado)</span>}
                        </p>
                        {getOptimizedValue('meta_title') && (
                          <span className="text-xs text-slate-500">{getOptimizedValue('meta_title')?.length || 0} caracteres</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Meta Description Comparison */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Meta Description (SEO)</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <span className="text-xs font-medium text-danger-600 dark:text-danger-400">ANTES</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                      {previewData.current.meta_description || <span className="text-slate-400 italic">Sin meta description</span>}
                    </p>
                    {previewData.current.meta_description && (
                      <span className="text-xs text-slate-500">{previewData.current.meta_description.length} caracteres</span>
                    )}
                  </div>
                  <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700 rounded-lg relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-success-600 dark:text-success-400">DESPUÉS</span>
                      {editingField !== 'meta_description' && !applyResult && (
                        <button
                          onClick={() => startEditing('meta_description')}
                          className="p-1 text-success-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                          title="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {editingField === 'meta_description' ? (
                      <div className="mt-1">
                        <textarea
                          value={editedValues.meta_description ?? ''}
                          onChange={(e) => setEditedValues(prev => ({ ...prev, meta_description: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-green-300 dark:border-green-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                          rows={3}
                          maxLength={160}
                          autoFocus
                        />
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs ${(editedValues.meta_description?.length || 0) > 160 ? 'text-danger-500' : 'text-slate-500'}`}>
                            {editedValues.meta_description?.length || 0}/160 caracteres
                          </span>
                          <div className="flex gap-2">
                            <button onClick={saveEdit} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                              <Check className="w-3 h-3" />
                            </button>
                            <button onClick={() => cancelEdit('meta_description')} className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className={`text-sm text-gray-900 dark:text-gray-100 mt-1 ${editedValues.meta_description !== undefined ? 'text-blue-700 dark:text-blue-300 font-medium' : ''}`}>
                          {getOptimizedValue('meta_description')}
                          {editedValues.meta_description !== undefined && <span className="text-xs text-primary-500 ml-2">(editado)</span>}
                        </p>
                        <span className="text-xs text-slate-500">{getOptimizedValue('meta_description')?.length || 0} caracteres</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Short Description Comparison */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Descripción corta</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <span className="text-xs font-medium text-danger-600 dark:text-danger-400">ANTES</span>
                    <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                      {previewData.current.description_short || <span className="text-slate-400 italic">Sin descripción corta</span>}
                    </p>
                  </div>
                  <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700 rounded-lg relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-success-600 dark:text-success-400">DESPUÉS</span>
                      {editingField !== 'description_short' && !applyResult && (
                        <button
                          onClick={() => startEditing('description_short')}
                          className="p-1 text-success-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                          title="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {editingField === 'description_short' ? (
                      <div className="mt-1">
                        <textarea
                          value={editedValues.description_short ?? ''}
                          onChange={(e) => setEditedValues(prev => ({ ...prev, description_short: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-green-300 dark:border-green-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                          rows={3}
                          maxLength={300}
                          autoFocus
                        />
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs ${(editedValues.description_short?.length || 0) > 300 ? 'text-danger-500' : 'text-slate-500'}`}>
                            {editedValues.description_short?.length || 0}/300 caracteres
                          </span>
                          <div className="flex gap-2">
                            <button onClick={saveEdit} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                              <Check className="w-3 h-3" />
                            </button>
                            <button onClick={() => cancelEdit('description_short')} className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className={`text-sm text-gray-900 dark:text-gray-100 mt-1 ${editedValues.description_short !== undefined ? 'text-blue-700 dark:text-blue-300 font-medium' : ''}`}>
                        {getOptimizedValue('description_short')}
                        {editedValues.description_short !== undefined && <span className="text-xs text-primary-500 ml-2">(editado)</span>}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Full Description Comparison */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Descripción completa</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Before */}
                  <div className="border border-red-200 dark:border-red-700 rounded-lg overflow-hidden">
                    <div className="bg-danger-50 dark:bg-danger-900/20 px-4 py-2 border-b border-red-200 dark:border-red-700">
                      <span className="font-medium text-danger-700 dark:text-red-300">ANTES</span>
                    </div>
                    <JvsProductHtmlPreview html={previewData.current.description_html} title="Descripción actual" />
                  </div>

                  {/* After */}
                  <div className="border border-green-200 dark:border-green-700 rounded-lg overflow-hidden">
                    <div className="bg-success-50 dark:bg-success-900/20 px-4 py-2 border-b border-green-200 dark:border-green-700 flex items-center justify-between">
                      <span className="font-medium text-green-700 dark:text-green-300">DESPUÉS</span>
                      {editingField !== 'description_html' && !applyResult && (
                        <button
                          onClick={() => startEditing('description_html')}
                          className="p-1 text-success-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                          title="Editar HTML"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {editingField === 'description_html' ? (
                      <div className="p-4">
                        <textarea
                          value={editedValues.description_html ?? ''}
                          onChange={(e) => setEditedValues(prev => ({ ...prev, description_html: e.target.value }))}
                          className="w-full px-3 py-2 text-sm font-mono border border-green-300 dark:border-green-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                          rows={15}
                          autoFocus
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button onClick={saveEdit} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1">
                            <Check className="w-4 h-4" /> Guardar
                          </button>
                          <button onClick={() => cancelEdit('description_html')} className="px-3 py-1.5 text-sm bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400 flex items-center gap-1">
                            <X className="w-4 h-4" /> Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        {editedValues.description_html !== undefined && (
                          <div className="absolute top-2 right-2 text-xs text-primary-500 bg-blue-100 dark:bg-blue-900/50 px-2 py-0.5 rounded">(editado)</div>
                        )}
                        <JvsProductHtmlPreview html={getOptimizedValue('description_html')} title="Descripción optimizada" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Capa AI-ready editable: respuesta directa, FAQ, etiquetas, alt */}
              {(() => {
                const geo = getGeoValues()
                const daWords = wordCount(geo.direct_answer)
                const disabled = !!applyResult
                return (
                  <div className="mb-6 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 space-y-4">
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Capa para motores de IA (ChatGPT, Perplexity, Gemini) y SEO
                    </h4>

                    {/* Respuesta directa */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Respuesta directa (citable, 40-60 palabras)</label>
                        <span className={`text-xs ${daWords >= 40 && daWords <= 60 ? 'text-success-600' : 'text-yellow-600'}`}>{daWords} palabras</span>
                      </div>
                      <textarea
                        value={geo.direct_answer}
                        disabled={disabled}
                        onChange={(e) => setGeoEdits(prev => ({ ...prev, direct_answer: e.target.value }))}
                        rows={3}
                        className="mt-1 w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60"
                      />
                    </div>

                    {/* FAQ */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Preguntas frecuentes (4-6)</label>
                        <span className={`text-xs ${geo.faqs.length >= 4 && geo.faqs.length <= 6 ? 'text-success-600' : 'text-yellow-600'}`}>{geo.faqs.length} preguntas</span>
                      </div>
                      <div className="space-y-2 mt-1">
                        {geo.faqs.map((f, i) => (
                          <div key={i} className="p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                            <div className="flex items-start gap-2">
                              <input
                                value={f.question}
                                disabled={disabled}
                                placeholder="Pregunta"
                                onChange={(e) => setGeoEdits(prev => {
                                  const faqs = [...geo.faqs]; faqs[i] = { ...faqs[i], question: e.target.value }; return { ...prev, faqs }
                                })}
                                className="flex-1 p-1.5 text-sm font-medium border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60"
                              />
                              {!disabled && (
                                <button
                                  type="button"
                                  onClick={() => setGeoEdits(prev => ({ ...prev, faqs: geo.faqs.filter((_, j) => j !== i) }))}
                                  className="p-1 text-slate-400 hover:text-danger-500"
                                  title="Quitar pregunta"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            <textarea
                              value={f.answer}
                              disabled={disabled}
                              placeholder="Respuesta (30-100 palabras, con dato concreto)"
                              rows={2}
                              onChange={(e) => setGeoEdits(prev => {
                                const faqs = [...geo.faqs]; faqs[i] = { ...faqs[i], answer: e.target.value }; return { ...prev, faqs }
                              })}
                              className="mt-1 w-full p-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60"
                            />
                          </div>
                        ))}
                        {!disabled && geo.faqs.length < 6 && (
                          <button
                            type="button"
                            onClick={() => setGeoEdits(prev => ({ ...prev, faqs: [...geo.faqs, { question: '', answer: '' }] }))}
                            className="text-xs text-primary-600 hover:underline"
                          >
                            + Añadir pregunta
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Tags */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Etiquetas (separadas por comas, 5-10)</label>
                        <input
                          value={geoEdits.tags ?? (previewData.optimized.tags || []).join(', ')}
                          disabled={disabled}
                          onChange={(e) => setGeoEdits(prev => ({ ...prev, tags: e.target.value }))}
                          className="mt-1 w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60"
                        />
                        <span className="text-xs text-slate-500">{geo.tags.length} etiquetas</span>
                      </div>
                      {/* Alt de imagen */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Texto alternativo de imagen (≤125)</label>
                        <input
                          value={geo.image_alt}
                          disabled={disabled}
                          maxLength={125}
                          onChange={(e) => setGeoEdits(prev => ({ ...prev, image_alt: e.target.value }))}
                          className="mt-1 w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60"
                        />
                        <span className={`text-xs ${geo.image_alt.length > 125 ? 'text-danger-500' : 'text-slate-500'}`}>{geo.image_alt.length}/125</span>
                      </div>
                    </div>

                    {previewData.optimized.mpn && (
                      <p className="text-xs text-slate-500 dark:text-slate-400">Part number del producto (mpn): <span className="font-mono text-slate-700 dark:text-slate-300">{previewData.optimized.mpn}</span></p>
                    )}
                  </div>
                )
              })()}

              {/* Gate AI-ready bloqueado (422): motivo + forzar */}
              {blockedReadiness && !applyResult && (
                <div className="mb-4 p-4 rounded-lg border border-red-300 dark:border-red-700 bg-danger-50 dark:bg-danger-900/20">
                  <div className="flex items-center gap-2 text-danger-700 dark:text-red-300 font-medium text-sm">
                    <AlertCircle className="w-4 h-4" />
                    No se ha aplicado: la ficha no cumple el contrato AI-ready (score {blockedReadiness.score}/100, mínimo 60).
                  </div>
                  <AiReadinessBadge readiness={blockedReadiness} className="mt-3" defaultOpen />
                  <label className="flex items-center gap-2 mt-3 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={forceApply} onChange={(e) => setForceApply(e.target.checked)} className="w-4 h-4 rounded" />
                    Aplicar de todos modos (force)
                  </label>
                </div>
              )}

              {/* Action Buttons */}
              {!applyResult && (
                <div className="mt-6">
                  {/* Apply Progress Indicator */}
                  {isApplying && applyProgress && (
                    <div className="mb-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        <span className="text-blue-800 dark:text-blue-200 font-medium">{applyProgress}</span>
                      </div>
                      <p className="text-xs text-primary-600 dark:text-primary-400 mt-2">
                        Este proceso traduce el contenido editado a portugués, inglés, francés y alemán antes de guardarlo.
                      </p>
                    </div>
                  )}

                  <div className="flex gap-4">
                    <button
                      onClick={() => setPreviewData(null)}
                      disabled={isApplying}
                      className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 disabled:opacity-50 flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Descartar
                    </button>
                    <button
                      onClick={handleApply}
                      disabled={isApplying}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      {isApplying ? 'Traduciendo y aplicando...' : (blockedReadiness && forceApply ? 'Aplicar forzando' : 'Confirmar y Traducir')}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    Al confirmar, el contenido en español se traducirá automáticamente a los demás idiomas (PT, EN, FR, DE).
                  </p>
                </div>
              )}

              {/* Apply Result */}
              {applyResult && (
                <div className={`mt-6 p-4 rounded-lg ${
                  applyResult.success
                    ? 'bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700'
                    : 'bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-700'
                }`}>
                  <div className="flex items-center gap-2">
                    {applyResult.success ? (
                      <Check className="w-5 h-5 text-success-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-danger-600" />
                    )}
                    <span className={applyResult.success ? 'text-green-700 dark:text-green-300' : 'text-danger-700 dark:text-red-300'}>
                      {applyResult.message}
                    </span>
                  </div>

                  {/* Capa AI-ready: score final, coherencia multi-idioma y post-pasos */}
                  {applyResult.success && (
                    <div className="mt-3 space-y-2">
                      {applyResult.ai_readiness && <AiReadinessBadge readiness={applyResult.ai_readiness} />}
                      {applyResult.multilang && (
                        <p className={`text-xs ${applyResult.multilang.passed ? 'text-success-700 dark:text-success-300' : 'text-yellow-700 dark:text-yellow-300'}`}>
                          Coherencia entre idiomas: {applyResult.multilang.passed ? 'correcta' : 'con diferencias'}
                          {!applyResult.multilang.passed && applyResult.multilang.details?.length > 0 && (
                            <> — {applyResult.multilang.details.join('; ')}</>
                          )}
                        </p>
                      )}
                      {applyResult.cache_purged === false && (
                        <p className="text-xs text-yellow-700 dark:text-yellow-300 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> Purga de caché no confirmada: los cambios pueden tardar en verse en la tienda.
                        </p>
                      )}
                      {applyResult.hooks_triggered === false && (
                        <p className="text-xs text-yellow-700 dark:text-yellow-300">Hooks de PrestaShop no ejecutados (reindexación pendiente).</p>
                      )}
                    </div>
                  )}

                  {/* Translation Cost Display */}
                  {applyResult.success && applyResult.openai_usage && (
                    <div className="mt-3">
                      <OpenAICostDisplay
                        usage={applyResult.openai_usage}
                        showDetails={true}
                        className="bg-success-100/50 dark:bg-green-900/30"
                      />
                      <p className="text-xs text-success-600 dark:text-success-400 mt-1">
                        Coste de traducción a 4 idiomas
                      </p>
                    </div>
                  )}

                  {applyResult.success && applyResult.backupId && (
                    <div className="mt-4 flex gap-4">
                      <button
                        onClick={handleRollback}
                        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Deshacer cambios
                      </button>
                      <a
                        href={`https://www.jvsinformatica.es/es/${selectedProduct.languages[1]?.link_rewrite || selectedProduct.id_product}.html`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center gap-2"
                      >
                        Ver en PrestaShop
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!selectedProduct && !isSearching && searchResults.length === 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-12 text-center">
          <Zap className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            Optimiza tus fichas de producto
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
            Busca un producto por ID o referencia para mejorar su presentación y SEO usando IA.
            Podrás revisar y editar el resultado en español antes de confirmar, y luego se traducirá a 5 idiomas.
          </p>
        </div>
      )}

      {/* Batch Optimization Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Optimización Masiva
              </h3>
              <button
                onClick={closeBatchModal}
                disabled={batchStatus?.status === 'running'}
                className="text-slate-500 hover:text-gray-700 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {!batchJobId ? (
                /* Configuration Form */
                <div className="space-y-4">
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                    <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                      ¿Estás seguro de que quieres optimizar {selectedProductIds.size} productos?
                    </p>
                    <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                      Este proceso puede tardar varios minutos. Cada producto será procesado con IA y traducido a 5 idiomas.
                    </p>
                  </div>

                  {/* Show global instructions if set */}
                  {instructions && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">
                        📝 Instrucciones que se aplicarán:
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300 italic">
                        "{instructions}"
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Duración de garantía
                      </label>
                      <select
                        value={batchWarranty}
                        onChange={(e) => setBatchWarranty(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                      >
                        <option value="6 meses">6 meses</option>
                        <option value="1 año">1 año</option>
                        <option value="2 años">2 años</option>
                        <option value="3 años">3 años</option>
                        <option value="5 años">5 años</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Aplicar cambios
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer mt-3">
                        <input
                          type="checkbox"
                          checked={batchAutoApply}
                          onChange={(e) => setBatchAutoApply(e.target.checked)}
                          className="w-4 h-4 text-primary-600 rounded"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          Aplicar automáticamente sin revisar
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Score AI-ready mínimo para aplicar
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={batchMinScore}
                        onChange={(e) => setBatchMinScore(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">0 = sin filtro. Por debajo del mínimo la ficha se omite.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Idiomas
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer mt-3">
                        <input
                          type="checkbox"
                          checked={batchTranslate}
                          onChange={(e) => setBatchTranslate(e.target.checked)}
                          className="w-4 h-4 text-primary-600 rounded"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          Traducir y aplicar en los 5 idiomas
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <p className="text-xs text-slate-500 mb-2">Productos a optimizar:</p>
                    <div className="space-y-1">
                      {searchResults
                        .filter(p => selectedProductIds.has(p.id_product))
                        .map(p => (
                          <div key={p.id_product} className="text-sm text-slate-700 dark:text-slate-300">
                            {p.reference} - {p.name}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Progress View */
                <div className="space-y-4">
                  {/* Overall Progress */}
                  <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-blue-800 dark:text-blue-200">
                        {batchStatus?.status === 'running' ? 'Procesando...' :
                         batchStatus?.status === 'completed' ? 'Completado' :
                         batchStatus?.status === 'cancelled' ? 'Cancelado' :
                         batchStatus?.status === 'failed' ? 'Error' : 'Iniciando...'}
                      </span>
                      <span className="text-sm text-primary-600 dark:text-primary-400">
                        {batchStatus?.processed || 0} / {batchStatus?.total_products || selectedProductIds.size}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${
                          batchStatus?.status === 'completed' ? 'bg-success-500' :
                          batchStatus?.status === 'failed' ? 'bg-danger-500' :
                          'bg-gradient-to-r from-yellow-400 to-yellow-500'
                        }`}
                        style={{
                          width: `${batchStatus ? (batchStatus.processed / batchStatus.total_products) * 100 : 0}%`
                        }}
                      />
                    </div>
                    {batchStatus?.current_product && (
                      <p className="text-sm text-primary-600 dark:text-primary-400 mt-2">
                        Procesando: {batchStatus.current_product}
                      </p>
                    )}
                  </div>

                  {/* Stats */}
                  {batchStatus && (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-success-50 dark:bg-success-900/20 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-success-600">{batchStatus.succeeded}</div>
                        <div className="text-xs text-green-700 dark:text-green-300">Completados</div>
                      </div>
                      <div className="bg-danger-50 dark:bg-danger-900/20 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-danger-600">{batchStatus.failed}</div>
                        <div className="text-xs text-danger-700 dark:text-red-300">Errores</div>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-slate-600 dark:text-slate-400">
                          {batchStatus.total_products - batchStatus.processed}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">Pendientes</div>
                      </div>
                    </div>
                  )}

                  {/* Product List with Status */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg max-h-60 overflow-y-auto">
                    {batchStatus?.products.map((product) => (
                      <div
                        key={product.product_id}
                        className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        {product.status === 'completed' && <Check className="w-4 h-4 text-success-500" />}
                        {product.status === 'failed' && <X className="w-4 h-4 text-danger-500" />}
                        {product.status === 'processing' && <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />}
                        {product.status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-slate-300" />}
                        {product.status === 'skipped' && <X className="w-4 h-4 text-slate-400" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-900 dark:text-white truncate">
                            {product.reference} - {product.name}
                          </div>
                          {product.error && (
                            <div className="text-xs text-danger-500 truncate">{product.error}</div>
                          )}
                        </div>
                        {typeof product.ai_readiness === 'number' && (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${scoreColor(product.ai_readiness).bg} ${scoreColor(product.ai_readiness).text}`}>
                            {product.ai_readiness}/100
                          </span>
                        )}
                        {product.status === 'skipped' && (
                          <span className="text-xs text-slate-500">omitida</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              {!batchJobId ? (
                <>
                  <button
                    onClick={closeBatchModal}
                    className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={startBatchOptimization}
                    className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-lg flex items-center gap-2"
                  >
                    <PlayCircle className="w-4 h-4" />
                    Iniciar Optimización
                  </button>
                </>
              ) : batchStatus?.status === 'running' ? (
                <button
                  onClick={cancelBatchJob}
                  className="px-4 py-2 bg-danger-500 hover:bg-red-600 text-white rounded-lg flex items-center gap-2"
                >
                  <StopCircle className="w-4 h-4" />
                  Cancelar proceso
                </button>
              ) : (
                <button
                  onClick={closeBatchModal}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
                >
                  Cerrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
